import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEARCH_KIND_LABELS, type SearchHit, type SearchIndex, type SearchKind } from '@/domain/search/search';
import { getSearchIndex } from '@/services/search';
import { useRequiredSession } from '../state/session';
import { useService } from '../hooks/useService';
import { useLoad } from '../hooks/useLoad';

const KINDS = Object.keys(SEARCH_KIND_LABELS) as SearchKind[];

/** 검색어를 굵게 (HTML 주입 없이 조각으로 나눔) */
function Highlight({ text, terms }: { text: string; terms: string[] }) {
  const ts = terms.filter(Boolean).map((t) => t.toLowerCase());
  if (!ts.length) return <>{text}</>;
  const out: { s: string; hit: boolean }[] = [];
  const lower = text.toLowerCase();
  let i = 0;
  while (i < text.length) {
    let next = -1;
    let len = 0;
    for (const t of ts) {
      const k = lower.indexOf(t, i);
      if (k >= 0 && (next < 0 || k < next)) {
        next = k;
        len = t.length;
      }
    }
    if (next < 0) {
      out.push({ s: text.slice(i), hit: false });
      break;
    }
    if (next > i) out.push({ s: text.slice(i, next), hit: false });
    out.push({ s: text.slice(next, next + len), hit: true });
    i = next + len;
  }
  return <>{out.map((p, k) => (p.hit ? <mark key={k}>{p.s}</mark> : <span key={k}>{p.s}</span>))}</>;
}

/** 검색 (UX_FLOW §15): 검색창 하나 + 필터, 결과는 출처와 함께, 누르면 그 위치로 */
export function SearchScreen() {
  const session = useRequiredSession();
  const svc = useService();
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const [version, setVersion] = useState(0);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [clientId, setClientId] = useState('');
  const [kinds, setKinds] = useState<SearchKind[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data: clients } = useLoad(() => svc.listClients(), [svc]);

  useEffect(() => {
    let cancelled = false;
    void getSearchIndex(session.repo, { refresh: version > 0 }).then((ix) => !cancelled && setIndex(ix));
    return () => {
      cancelled = true;
    };
  }, [session.repo, version]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  const hits: SearchHit[] = useMemo(
    () =>
      index && debounced.trim()
        ? index.search(debounced, {
            ...(clientId ? { clientId } : {}),
            ...(kinds.length ? { kinds } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          })
        : [],
    [index, debounced, clientId, kinds, from, to],
  );
  const terms = debounced.trim().split(/\s+/);
  const fuzzy = hits.length > 0 && hits[0]!.fuzzy;

  return (
    <section className="stack">
      <div className="page-head">
        <h1>검색</h1>
        <button type="button" className="link" onClick={() => (setIndex(null), setVersion((v) => v + 1))}>
          새로고침
        </button>
      </div>
      <input
        type="search"
        aria-label="검색어"
        placeholder="전사·메모·기록지·사례개념화·목표·문서에서 찾기"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      <div className="row search-filters">
        <select aria-label="내담자" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">모든 내담자</option>
          {clients?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.clientCode}
              {c.alias ? ` (${c.alias})` : ''}
            </option>
          ))}
        </select>
        <input type="date" aria-label="시작 날짜" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span>~</span>
        <input type="date" aria-label="끝 날짜" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      <div className="chips" role="group" aria-label="자료 종류">
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={`chip ${kinds.includes(k) ? 'chip-on' : ''}`}
            aria-pressed={kinds.includes(k)}
            onClick={() => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]))}
          >
            {SEARCH_KIND_LABELS[k]}
          </button>
        ))}
      </div>

      {!index ? (
        <p className="muted">검색 준비 중… (기록을 이 기기 메모리에서만 읽습니다)</p>
      ) : !debounced.trim() ? (
        <p className="muted small">
          기록 {index.size.toLocaleString()}건에서 찾습니다. 띄어쓰기는 무시되고, 단어 일부로도 찾습니다.
        </p>
      ) : hits.length === 0 ? (
        <p className="muted">찾는 내용이 없습니다.</p>
      ) : (
        <>
          <p className="muted small">
            {hits.length >= 100 ? '100건 이상' : `${hits.length}건`}
            {fuzzy && ' · 정확히 일치하는 결과가 없어 비슷한 결과를 보여줍니다'}
          </p>
          <ul className="list search-results">
            {hits.map((h) => (
              <li key={h.doc.id}>
                <Link to={h.doc.link} className="search-hit">
                  <span className="small">
                    <strong>{h.doc.clientCode}</strong> · {h.doc.source}
                    {h.doc.date && <span className="muted"> · {h.doc.date}</span>}
                  </span>
                  <span className="search-snippet">
                    <Highlight text={h.snippet} terms={terms} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
